import type { WorkflowDefinition } from "../types";

/**
 * W24 — Fix two of your own pages competing for one search.
 *
 * `perTarget` on the page's pipeline run, like W22 and W23: the pair is a
 * property of one search, not of the property, and two people can be untangling
 * two different splits in the same week.
 *
 * Three of the five steps are `manual`. Reading an overlap warning, choosing
 * which page should win and merging the other one all happen in a CMS and in a
 * meeting, and nothing the runner can see is a fair proxy for any of them.
 */
export const competingPagesWorkflow: WorkflowDefinition = {
	id: "competing-pages",
	wNumber: 24,
	title: "Fix pages competing for the same search",
	outcome: "Stop two of your own pages splitting one search between them.",
	audiences: ["content", "inhouse"],
	uses: ["Citation Optimizer", "Property Pages"],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "pipelineRun", label: "Page", pipelineMode: "page" },
	// Same gate as W22: a page is scored against a grounding search, which a
	// PERSON's identity prompts never emit.
	isApplicable: (mode) => mode.showGroundingSearches,
	articleSlug: "how-to-fix-pages-competing-for-the-same-query",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "score-page",
			title: "Score the page you want to win",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've scored a page for this workflow.",
			why: "Start from the search, not from a list of addresses. Scoring the page you would rather keep is what surfaces the pair.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Pick a search two of your pages both come back for",
				"Score the page you would rather keep",
				"Read the stoplight strip before you look at the pair",
			],
			complete: {
				kind: "all",
				of: [
					{
						kind: "since",
						event: "pipelineRunCreated",
						scope: "target",
					},
					{ kind: "count", key: "pipeline.page", gte: 1 },
				],
			},
			signal: ({ state }) => {
				const score =
					state.target?.pipeline?.latestScore ??
					state.property?.pipeline.latestScore ??
					null;
				if (score === null) {
					return null;
				}
				return {
					kind: "score",
					label: "Citation score",
					value: score,
					target: 70,
				};
			},
		},
		{
			id: "read-overlap",
			title: "Read the overlap warning",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've read the overlap warning on the pair.",
			why: "Two pages on one topic only compete when they make the same promise. The warning waits for the body and the summary to match.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Open the flagged pair and compare the first 200 words",
				"Compare the titles and meta descriptions too",
				"Write one sentence describing the overlap",
			],
			// Judgement call: nothing watches "you read the warning".
			complete: { kind: "manual" },
		},
		{
			id: "choose-winner",
			title: "Choose the page that should win",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've chosen the page that should win.",
			why: "Pick one page and write down why. Position, links and format decide it, not which page reads better today.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Favor the page that already sits closer to the top",
				"Check what links to each one from outside",
				"Keep the address with the history, even if the writing is older",
			],
			complete: { kind: "manual" },
		},
		{
			id: "fix-other",
			title: "Change or merge the other page",
			page: "Property Pages",
			route: "/pages",
			criterion: "You've merged or reworked the other page.",
			why: "Merging moves the useful parts across and redirects the old address. Changing it means a new title, a new opening and a different reader.",
			docsHref: "/docs/dashboards/property-pages",
			tasks: [
				"Merge and redirect for good, or give the page a new angle",
				"Point internal links at the page you kept",
				"Rewrite the opening so one page owns the whole question",
			],
			complete: { kind: "manual" },
		},
		{
			id: "rescore-both",
			title: "Score both pages again",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've scored both pages for this workflow.",
			why: "Nothing moves until both pages are crawled again. Three to six weeks is normal, and a dip before the recovery is normal too.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Score the page you kept against the same search",
				"Score the other page so you can watch it drop away",
				"Count how many of your pages still come back",
			],
			complete: {
				kind: "all",
				of: [
					{
						kind: "since",
						event: "pipelineRunCompleted",
						scope: "target",
					},
					{ kind: "count", key: "pipeline.page", gte: 2 },
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Pages scored",
							value: state.property.pipeline.page,
							target: 2,
						}
					: null,
		},
	],
};
